import { useMemo } from 'react';
import { subDays, format } from 'date-fns';
import { HabitLog, LogStatus } from '../../types';

export interface GeneralTrendDataPoint {
  date: string;
  displayDate: string;
  successRate: number;  // 0-1 scale (rolling average)
  rawDone: number;      // Raw daily count
  rawTotal: number;     // Raw daily total
}

interface RawDayData {
  done: number;
  total: number;
}

const ROLLING_WINDOW = 7;

/**
 * Calculate 7-day rolling average for success rate.
 */
const calculateRollingAverage = (
  rawData: RawDayData[],
  currentIndex: number
): number => {
  const startIdx = Math.max(0, currentIndex - ROLLING_WINDOW + 1);
  
  let totalDone = 0;
  let totalAttempts = 0;
  
  for (let i = startIdx; i <= currentIndex; i++) {
    const day = rawData[i];
    if (!day) continue;
    totalDone += day.done;
    totalAttempts += day.total;
  }
  
  if (totalAttempts === 0) return 0;
  return Math.round((totalDone / totalAttempts) * 100) / 100;
};

/**
 * Calculate dynamic date range based on user's app usage history.
 */
const calculateDateRange = (allLogs: HabitLog[]): { startDate: Date; dayCount: number } => {
  if (allLogs.length === 0) {
    return { startDate: subDays(new Date(), 89), dayCount: 90 };
  }
  
  const earliestTimestamp = Math.min(...allLogs.map(l => new Date(l.date).getTime()));
  const earliest = new Date(earliestTimestamp);
  const today = new Date();
  
  const daysSinceStart = Math.floor((today.getTime() - earliest.getTime()) / (1000 * 60 * 60 * 24));
  
  if (daysSinceStart < 90) {
    return { startDate: earliest, dayCount: daysSinceStart + 1 };
  }
  
  return { startDate: subDays(today, 89), dayCount: 90 };
};

/**
 * Hook to process Regular/Rawatib habit logs into success rate trend data.
 * Shows data from app start until 90 days of usage, then switches to rolling 90-day window.
 * 
 * @param logs - All habit logs
 * @param habitId - The specific habit ID to track
 * @param requestedDays - Optional override for number of days
 */
export const useGeneralTrends = (
  logs: HabitLog[],
  habitId: string,
  requestedDays?: number
): GeneralTrendDataPoint[] => {
  return useMemo(() => {
    const today = new Date();
    
    // Filter logs for this specific habit
    const habitLogs = logs.filter(l => l.habitId === habitId);
    
    // Calculate dynamic date range
    const { startDate, dayCount } = requestedDays 
      ? { startDate: subDays(today, requestedDays - 1), dayCount: requestedDays }
      : calculateDateRange(logs);
    
    // Group logs by date
    const logsByDate: Record<string, HabitLog[]> = {};
    habitLogs.forEach(log => {
      if (!logsByDate[log.date]) {
        logsByDate[log.date] = [];
      }
      logsByDate[log.date]!.push(log);
    });
    
    // First pass: collect raw daily counts
    const rawData: (RawDayData & { dateStr: string; displayDate: string })[] = [];
    
    for (let i = 0; i < dayCount; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      const dateStr = format(date, 'yyyy-MM-dd');
      const displayDate = format(date, 'MMM d');
      
      const dayLogs = logsByDate[dateStr] || [];
      const done = dayLogs.filter(l => l.status === LogStatus.DONE).length;
      const total = dayLogs.length;
      
      rawData.push({
        dateStr,
        displayDate,
        done,
        total,
      });
    }
    
    // Second pass: calculate rolling averages
    const dataPoints: GeneralTrendDataPoint[] = rawData.map((day, index) => {
      const successRate = calculateRollingAverage(rawData, index);
      
      return {
        date: day.dateStr,
        displayDate: day.displayDate,
        successRate,
        rawDone: day.done,
        rawTotal: day.total,
      };
    });
    
    return dataPoints;
  }, [logs, habitId, requestedDays]);
};

export default useGeneralTrends;
